import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { Wrench, ArrowRight } from "@phosphor-icons/react";
import { SocialSignIn } from "@/components/SocialSignIn";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const HERO =
  "https://images.unsplash.com/photo-1781559818983-4180d744416a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwzfHx3YXJlaG91c2UlMjB3b3JrZXIlMjB0YWJsZXR8ZW58MHx8fHwxNzg2NzIzNDY3fDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login, verifyTwoFactor } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [challengeId, setChallengeId] = useState(location.state?.challengeId || null);
  const [code, setCode] = useState("");

  const done = (user) => {
    if (user?.must_change_password) navigate("/privacy", { state: { forcePassword: true } });
    else navigate(location.state?.from || "/dashboard");
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const res = await login(email, password);
    setBusy(false);
    if (res.ok) done(res.user);
    else if (res.twofa) setChallengeId(res.challengeId);
    else setError(res.error);
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const res = await verifyTwoFactor(challengeId, code.trim());
    setBusy(false);
    if (res.ok) done(res.user);
    else setError(res.error);
  };

  return (
    <div className="flex min-h-screen">
      <div className="flex w-full flex-col justify-center px-6 py-12 sm:px-12 lg:w-[52%] lg:px-20">
        <div className="mx-auto w-full max-w-md">
          <div className="mb-10 flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center bg-[#002FA7]">
              <Wrench size={20} weight="bold" className="text-white" />
            </div>
            <div className="leading-tight">
              <div className="font-display text-sm font-extrabold tracking-tight">
                UNITY &amp; UTILITY
              </div>
              <div className="overline text-[0.6rem] text-slate-500">Floor Operations</div>
            </div>
          </div>

          <div className="overline mb-2 text-[#002FA7]">Member access</div>
          <h1 className="font-display text-4xl font-bold tracking-tight sm:text-5xl">Sign in.</h1>
          <p className="mt-3 text-sm text-slate-500">
            One place for inventory, vendors, buyers, feedback and floor ideas.
          </p>

          {challengeId ? (
            <form onSubmit={submitCode} className="mt-8 space-y-4" data-testid="twofa-form">
              <p className="text-sm text-slate-500">
                {email ? <>We emailed a 6-digit code to <span className="font-semibold text-[#0F172A]">{email}</span>. It expires in 10 minutes.</> : "We emailed you a 6-digit code. It expires in 10 minutes."}
              </p>
              <div>
                <Label htmlFor="code" className="overline text-slate-500">Sign-in code</Label>
                <Input
                  id="code"
                  data-testid="twofa-code-input"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                  className="mt-1.5 rounded-none border-slate-300 font-mono tracking-[0.4em]"
                  placeholder="000000"
                />
              </div>
              {error && (
                <div data-testid="twofa-error" className="border border-[#F6B9B5] bg-[#FFECEB] px-3 py-2 text-sm text-[#B3241B]">
                  {error}
                </div>
              )}
              <Button
                data-testid="twofa-submit-button"
                type="submit"
                disabled={busy}
                className="w-full rounded-none bg-[#002FA7] py-7 text-sm font-semibold uppercase tracking-widest text-white hover:bg-[#00227A] sm:py-6"
              >
                {busy ? "Checking…" : "Verify and sign in"}
                <ArrowRight size={16} className="ml-2" />
              </Button>
              <button
                type="button"
                data-testid="twofa-cancel-button"
                onClick={() => { setChallengeId(null); setCode(""); setError(""); }}
                className="text-xs text-slate-500 hover:underline"
              >
                Use a different account
              </button>
            </form>
          ) : (
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="overline text-slate-500">Email</Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1.5 rounded-none border-slate-300"
                placeholder="you@company.com"
              />
            </div>
            <div>
              <Label htmlFor="password" className="overline text-slate-500">Password</Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1.5 rounded-none border-slate-300"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="border border-[#F6B9B5] bg-[#FFECEB] px-3 py-2 text-sm text-[#B3241B]"
              >
                {error}
              </div>
            )}

            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={busy}
              className="w-full rounded-none bg-[#002FA7] py-7 text-sm font-semibold uppercase tracking-widest text-white hover:bg-[#00227A] sm:py-6"
            >
              {busy ? "Signing in…" : "Sign in"}
              <ArrowRight size={16} className="ml-2" />
            </Button>
          </form>
          )}

          <SocialSignIn />

          <p className="mt-6 text-sm text-slate-500">
            <Link data-testid="go-to-forgot-link" to="/forgot-password" className="font-semibold text-[#002FA7] hover:underline">
              Forgotten your password?
            </Link>
          </p>

          <p className="mt-3 text-sm text-slate-500">
            No account?{" "}
            <Link data-testid="go-to-register-link" to="/register" className="font-semibold text-[#002FA7] hover:underline">
              Create one
            </Link>
          </p>

          <p className="mt-8 text-xs text-slate-500" data-testid="login-access-note">
            Access is by account only. Ask your administrator for an invite code if you do not have one.          </p>
        </div>
      </div>

      <div className="relative hidden lg:block lg:w-[48%]">
        <img src={HERO} alt="Warehouse operator" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#0F172A]/65" />
        <div className="absolute bottom-0 left-0 p-12">
          <div className="overline mb-3 text-[#7FA0FF]">Unity in one system</div>
          <p className="font-display max-w-md text-3xl font-bold leading-tight tracking-tight text-white">
            Materials, consumables, tools and equipment — tracked by the people on the floor.
          </p>
        </div>
      </div>
    </div>
  );
}
